import React, { useContext, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import template from 'lodash.template'
import { Form, StepHeading } from 'components/GetStarted/styles'
import { trackClickEvent } from 'utils/Analytics'
import TextAreaInput from 'components/generic/Input/TextAreaInput'
import { getHeadingForUserType, getRandomFacetDescription } from 'utils/getStartedUtils'
import AUTO_POPULATE from 'components/api/queries/GetStartedDetails.gql'
import CREATE_LISTING from 'components/api/mutations/createListing.gql'
import { useQuery, useMutation } from '@apollo/client'
import InputErrorMessage from 'components/generic/Input/InputErrorMessage'
import { InputHint } from './styles'
import { USER_TYPES, RATE_TYPES } from 'components/GetStarted/constants'
import { AppContext } from 'contexts/AppContext'
import LoadingIndicatorButton from 'components/generic/LoadingIndicatorButton'
import FormSubmissionError from 'components/generic/FormSubmissionError'
import getSentry from 'utils/Sentry'

const { captureException } = getSentry()

const HEADLINE_MAX_LENGTH = 125
const DESCRIPTION_MIN_LENGTH = 100
const DESCRIPTION_MAX_LENGTH = 5000

const StepEnterDetails = ({ data, userInput, onStepComplete, refetchData }) => {
  const { domain } = useContext(AppContext)
  const { register, handleSubmit, errors, setValue, formState, watch } = useForm({
    mode: 'all',
    defaultValues: {
      name: userInput?.name,
      description: userInput?.description,
    },
  })
  const userType = userInput?.user_type
  const { data: defaultInputValues } = useQuery(AUTO_POPULATE, {
    variables: { userType: userType },
    skip: !userType,
  })
  const [createListing, { error: createListingError }] = useMutation(CREATE_LISTING)
  const { isDirty, isSubmitting, isValid } = formState

  const watchDescription = watch('description', '')
  const descriptionCharMinCount = DESCRIPTION_MIN_LENGTH - watchDescription?.trim().length
  const heading = getHeadingForUserType({
    userType,
    providerHeading: `What should ${data?.site?.plural_suffixed_seeker_name} know about the job?`,
    seekerHeading: `What should ${data?.site?.plural_provider_name} know about you?`,
    defaultHeading: 'What should they know about you?',
  })
  const descriptionSublabel = getHeadingForUserType({
    userType,
    providerHeading: `Describe what services you provide & what you're looking for in a job`,
    seekerHeading: `Describe what services you need & who you're looking to hire`,
    defaultHeading: `Describe what you're looking for`,
  })
  const headlineValidationRules = {
    required: 'Please enter a headline.',
    validate: {
      maxLength: (value) =>
        value?.trim().length <= HEADLINE_MAX_LENGTH || `Max. ${HEADLINE_MAX_LENGTH} characters`,
    },
  }
  const descriptionValidationRules = {
    required: 'Please enter description.',
    validate: {
      minLength: (value) =>
        value?.trim().length >= DESCRIPTION_MIN_LENGTH ||
        `Min. ${DESCRIPTION_MIN_LENGTH} characters - Enter ${descriptionCharMinCount} more`,
      maxLength: (value) =>
        value?.trim().length <= DESCRIPTION_MAX_LENGTH ||
        `Max. ${DESCRIPTION_MAX_LENGTH} characters`,
    },
  }
  const onSubmit = async ({ name, description }) => {
    try {
      await createListing({
        variables: {
          input: {
            is_provider_listing: userType === USER_TYPES.PROVIDER,
            name,
            description,
            city_id: Number(userInput?.city),
            origin: userInput?.origin,
            services: userInput?.services,
            work_eligibility_sponsorship_required: userInput?.sponsorship,
            wage_info: userInput?.hourly_rate,
            wage_type: userInput?.rate_type || RATE_TYPES.HOURLY,
          },
        },
      })
      await refetchData()
      onStepComplete()
    } catch (err) {
      captureException(err)
    }
  }

  useEffect(() => {
    const defaultHeading = defaultInputValues?.site?.headline
    const defaultDescription = defaultInputValues?.site?.description

    if (defaultDescription && defaultHeading && !isDirty) {
      const templateValues = {
        first_name: userInput?.first_name ?? '',
        state: userInput?.location?.state?.name ?? '',
        city: userInput?.location?.city?.name ?? '',
        facet: getRandomFacetDescription(userInput?.services, data),
        facet_lower: getRandomFacetDescription(userInput?.services, data).toLowerCase(),
        is_provider:
          userInput?.user_type === USER_TYPES.PROVIDER || data?.viewer?.user?.is_provider,
      }

      // The lodash.template method will error out if one of the template variables is missing
      // This ensures the app can continue if that happens
      try {
        setValue('name', template(defaultHeading)(templateValues), {
          shouldDirty: true,
          shouldValidate: true,
        })
        setValue('description', template(defaultDescription)(templateValues), {
          shouldDirty: true,
          shouldValidate: true,
        })
      } catch (err) {
        captureException(err)
      }
    }
  }, [defaultInputValues, isDirty, userInput, setValue, data])

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <StepHeading>{heading}</StepHeading>
      <FormSubmissionError
        isVisible={createListingError}
        heading='We were unable to create your profile.'
      >
        Please try again or contact <a href='mailto:info@careguide.com'>info@careguide.com</a> for
        assistance.
      </FormSubmissionError>
      <TextAreaInput
        id='details-name'
        name='name'
        placeholder='A catchy descriptive headline receives more responses'
        rows='2'
        autoFocus={true}
        disabled={isSubmitting}
        label='Headline'
        subLabel="State who you are & what you're offering"
        isValid={!errors.name}
        ref={register(headlineValidationRules)}
      />
      <InputErrorMessage errors={errors} inputId='details-name' name='name' />
      <TextAreaInput
        id='details-description'
        name='description'
        placeholder='Descriptions between 300-400 words tend to get more views'
        rows='5'
        disabled={isSubmitting}
        label='Description'
        isValid={!errors.description}
        subLabel={descriptionSublabel}
        ref={register(descriptionValidationRules)}
      />
      <InputErrorMessage errors={errors} inputId='details-description' name='description' />
      {!errors.description && (
        <InputHint htmlFor='details-description'>{`Min. 100 characters - ${
          watchDescription?.trim().length >= DESCRIPTION_MIN_LENGTH
            ? 'Reached!'
            : descriptionCharMinCount
        }`}</InputHint>
      )}
      <LoadingIndicatorButton
        mt='2rem'
        disabled={!isValid}
        onClick={() => trackClickEvent(domain, 'get-started-next-details')}
        isLoading={isSubmitting}
      >
        {isSubmitting ? 'Creating listing' : 'Next'}
      </LoadingIndicatorButton>
    </Form>
  )
}

export default StepEnterDetails
